






















































import { getComBoxProgramName } from "@/data/models/ComBox";
import {
  getHyDryEventCategoryDescription,
  getHyDryEventDescription,
  getHyDryPanelEventDescription,
  getHyDryEventFromEventNumber,
  getHyDryPanelEventFromEventNumber,
  HyDryEvent,
  HyDryPanelEvent,
  HyDryEventCategory,
} from "@/data/models/HyDryEvent";
import { HyDryEventLog } from "@/data/models/HyDryEventLog";
import {
  getIresDeviceCategory,
  IresDevice,
  IresDeviceCategory,
} from "@/data/models/IresDevice";
import Panel from "@/data/models/Panel";
import { getPanelProgramName } from "@/data/models/PanelConfig";
import { ProgramChain } from "@/data/models/ProgramChain";
import { viewCycleInformationAccess, viewStepInformationAccess } from "@/permissions/UserPermissions";
import { formatDateHoursMinutes } from "@/utils/DateFormatter";
import { joinToString } from "@/utils/LocalisationHelper";
import { Vue, Component, Prop } from "vue-property-decorator";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component
export default class HyDryEventLogItemComponent extends Vue {
  @Prop({ default: false })
  compact!: boolean;

  @Prop({ default: false })
  showNote!: boolean;

  @Prop()
  iresDevice!: IresDevice;

  @Prop()
  programChain!: ProgramChain;

  @Prop()
  hyDryEventLog!: HyDryEventLog;
  @Prop() doesSupportExtendedSensors!: boolean;

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get highlightDate() {
    return this.showNote && !!this.hyDryEventLog.note;
  }

  get iresDeviceCategory(): IresDeviceCategory {
    return getIresDeviceCategory(this.iresDevice);
  }

  get isAllowedViewCycleInformation() {
    return viewCycleInformationAccess(this.activeUser);
  }

  get isAllowedViewStepInformation() {
    return viewStepInformationAccess(this.activeUser);
  }

  get isPanel() {
    return this.iresDeviceCategory === IresDeviceCategory.PANEL;
  }

  get event() {
    return this.isPanel
      ? getHyDryPanelEventFromEventNumber(this.hyDryEventLog.eventNumber)
      : getHyDryEventFromEventNumber(this.hyDryEventLog.eventNumber);
  }

  /** Phone layout (below 600 px): the entry becomes a caption line over the text. */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The phone caption after the time: "Pause · Unterdruck · P3/Z9", skipping what is unknown. */
  get phoneLogCaptionRest() {
    return [this.categoryDescription, this.programName, this.stepAndCycle]
      .filter((part) => !!part)
      .join(" · ");
  }

  get eventDate() {
    return formatDateHoursMinutes(this.hyDryEventLog.eventDate.toDate());
  }

  get categoryDescription() {
    if (this.hyDryEventLog.eventCategory === HyDryEventCategory.NOTE) {
      return "";
    }

    return getHyDryEventCategoryDescription(
      this,
      this.hyDryEventLog.eventCategory,
      undefined,
      this.isPanel ? undefined : this.hyDryEventLog.eventNumber
    );
  }

  get stepAndCycle(): string {
    const step = this.hyDryEventLog.step;
    const cycle = this.hyDryEventLog.cycle;

    switch (this.iresDeviceCategory) {
      case IresDeviceCategory.COMBOX: {
        return joinToString(
          [
            step === undefined || !this.isAllowedViewStepInformation
              ? undefined
              : this.$t("hyDryEventLog.formatPhase", [step + 1]).toString(),
            cycle === undefined || cycle === 255 || !this.isAllowedViewCycleInformation
              ? undefined
              : this.$t("hyDryEventLog.formatCycle", [cycle]).toString(),
          ],
          "/"
        );
      }
      case IresDeviceCategory.PANEL: {
        return joinToString(
          [
            step === undefined || !this.isAllowedViewStepInformation
              ? undefined
              : this.$t("hyDryEventLog.formatStep", [step]).toString(),
            cycle === undefined || cycle === 255 || !this.isAllowedViewCycleInformation
              ? undefined
              : this.$t("hyDryEventLog.formatCycle", [cycle]).toString(),
          ],
          "/"
        );
      }
    }

    return "";
  }

  get eventDescription() {
    return this.isPanel 
      ? getHyDryPanelEventDescription(
        this,
        this.event as HyDryPanelEvent,
        this.iresDevice
      ).toString()
      : getHyDryEventDescription(
        this,
        this.event as HyDryEvent,
        this.hyDryEventLog.additionalData,
        true,
        undefined,
        // ==================== FIRMWARE v4.0.0+ CHANGES ====================
        this.doesSupportExtendedSensors
      ).toString();
  }

  get programName(): string | undefined {
    if (this.hyDryEventLog.program === undefined) {
      return undefined;
    }

    const event = this.event;

    if (
      !this.hyDryEventLog.dryRunActive &&
      this.iresDeviceCategory !== IresDeviceCategory.PANEL &&
      event !== HyDryEvent.DRYING_ENDED &&
      event !== HyDryEvent.DRYING_PROGRAM_DONE &&
      event !== HyDryEvent.DRYING_PROGRAM_TERMINATED
    ) {
      return undefined;
    }

    switch (this.iresDeviceCategory) {
      case IresDeviceCategory.COMBOX: {
        return getComBoxProgramName(this, this.hyDryEventLog.program);
      }
      case IresDeviceCategory.PANEL: {
        return getPanelProgramName(
          this,
          this.hyDryEventLog.program,
          this.iresDevice as Panel,
          null
        );
      }
    }

    return "";
  }
}
